<template>
	<Spinner v-if="loadingCheckout" />
	<div v-show="!loadingCheckout">
		<!-- Customer Details -->
		<CheckoutDetails id="customerDetails" title="Customer">
			<template v-if="!showCustomerDetails" v-slot:email>
				<div class="w-full mt-2 md:mt-0 md:pl-4 md:mx-4 text-sm text-ihh-midnight-gray flex items-center">{{ store?.user?.email || guest.email }}</div>
			</template>

			<template v-if="store?.user?.id || guest.email" v-slot:button>
				<div v-if="store?.user?.id">
					<Login v-slot="{ logout }" small="true" class="!p-0">
						<button
							@click="logout"
							class="w-24 hover:bg-ihh-gray/10 group-hover:border-ihh-midnight-gray/50 text-center text-sm text-ihh-midnight-gray border rounded-sm border-ihh-gray/50 py-1.5"
							v-html="`Sign Out`"
						/>
					</Login>
				</div>
				<div v-else>
					<button
						v-if="!showCustomerDetails"
						@click="showCustomerDetails = true"
						class="w-24 hover:bg-ihh-gray/10 group-hover:border-ihh-midnight-gray/50 text-center text-sm text-ihh-midnight-gray border rounded-sm border-ihh-gray/50 py-1.5"
					>
						Edit
					</button>
				</div>
			</template>

			<template v-if="showCustomerDetailsSlot" v-slot:details>
				<CheckoutLogin :showCustomerDetails="showCustomerDetailsSlot" @customer-continue="handleCustomerContinue" />
			</template>
		</CheckoutDetails>

		<!-- Shipping Details -->
		<CheckoutDetails id="shippingDetails" title="Shipping" v-if="!isDigitalOnly">
			<template v-slot:button>
				<button
					@click="showShippingDetails = !showShippingDetails"
					v-html="showShippingDetails ? 'Hide' : 'Edit'"
					class="w-24 hover:bg-ihh-gray/10 group-hover:border-ihh-midnight-gray/50 text-center text-sm text-ihh-midnight-gray border rounded-sm border-ihh-gray/50 py-1.5"
				></button>
			</template>

			<template v-slot:summary>
				<AddressSummary
					v-if="!showShippingDetails"
					:address="checkout.shipping.shippingAddress"
					:shippingMethod="checkout.shipping.selectedShipping"
					:type="'shipping'"
				/>
			</template>

			<template v-slot:details>
				<div v-show="showShippingDetails" class="my-8 w-full" :class="{ 'opacity-40': savingShippingAddress }">
					<ShippingAddressDetails
						:addresses="checkout.addresses"
						:selectedAddress="checkout.shipping.shippingAddress"
						:enableAutoComplete="googleMapsLoaded"
						:loadingShipping="loadingShipping"
						v-model:hideShippingAddressForm="hideShippingAddressForm"
						@saveNewAddress="updateSaveNewShippingAddress"
						@continue="handleShippingContinue"
						@addressSelected="handleAddressSelected"
					/>
				</div>
			</template>
		</CheckoutDetails>

		<!-- Billing Details -->
		<CheckoutDetails id="billingDetails" title="Billing">
			<template v-slot:button>
				<button
					@click="showBillingDetails = !showBillingDetails"
					v-html="showBillingDetails ? 'Hide' : 'Edit'"
					class="w-24 hover:bg-ihh-gray/10 group-hover:border-ihh-midnight-gray/50 text-center text-sm text-ihh-midnight-gray border rounded-sm border-ihh-gray/50 py-1.5"
				></button>
			</template>

			<template v-slot:summary>
				<AddressSummary :address="checkout.billing.billingAddress" v-if="!showBillingDetails" />
			</template>

			<template v-slot:details>
				<div v-show="showBillingDetails" :class="{ 'opacity-40': savingBillingAddress }">
					<BillingAddressDetails
						:addresses="checkout.addresses"
						:selectedAddress="checkout.billing.billingAddress"
						:enableAutoComplete="googleMapsLoaded"
						:showBillingDetails="showBillingDetails"
						@saveNewAddress="updateSaveNewBillingAddress"
						@continue="handleBillingContinue"
					/>
				</div>
			</template>
		</CheckoutDetails>

		<!-- International Shipping Disclaimer Modal -->
		<Modal maxWidth="max-w-3xl" :active="showInternationalDisclaimer" @hide="showInternationalDisclaimer = false" :warning="true" :hideX="true">
			<template v-slot>
				<div class="space-y-3 text-ihh-warning-text">
					<!-- underline offset on this line -->
					<div class="flex w-full justify-center text-ihh-warning-text font-semibold text-xl sm:text-2xl">International Shipping Notice</div>
					<div>
						While we make every effort to ensure delivery in a timely manner we
						<span class="ml-1 font-semibold underline underline-offset-2">cannot guarantee delivery of any order shipped outside the United States.</span>
					</div>
					<div>
						UPS shipping services are generally more reliable than USPS services and provide better tracking. The recipient is also responsible for any
						customs/duties fees due. Additionally, we also do not accept returns on international orders. Please
						<router-link to="/help-center" class="text-ihh-warning-link underline">contact us</router-link> if you have any questions and thank you for your
						understanding.
					</div>
					<div class="flex w-full justify-center">
						<button @click="showInternationalDisclaimer = false" class="bg-ihh-warning-text text-white py-2 px-4 rounded">I Understand</button>
					</div>
				</div>
			</template>
		</Modal>
	</div>
</template>

<script>
// Libraries
import { debounce } from 'lodash'
import Swal from 'sweetalert2'

// Components
import CheckoutDetails from '../pages/ihh-items/checkout-details.vue'
import CheckoutLogin from './checkout-login.vue'
import Login from '../layout/login.vue'
import AddressSummary from './address-summary.vue'
import ShippingAddressDetails from './shipping-address-details.vue'
import { useCheckoutStore } from '../../stores/store.js'
import BillingAddressDetails from './billing-address-details.vue'
import ShippingMethods from './shipping-methods.vue'
import PaymentTable from './payment-table.vue'
import OrderSummary from './order-summary.vue'

// Helpers
import helpers from '../../helpers/checkout.js'
import Mango from '../../helpers/mango.js'

export default {
	name: 'ShippingAndBilling',

	emits: ['showPayment'],

	components: {
		CheckoutDetails,
		Login,
		CheckoutLogin,
		AddressSummary,
		BillingAddressDetails,
		ShippingAddressDetails,
		ShippingMethods,
		PaymentTable,
		OrderSummary,
	},
	inject: ['store'],

	props: {
		isDigitalOnly: {
			type: Boolean,
			default: false,
		},
	},

	data() {
		return {
			checkout: useCheckoutStore(),
			// UI Variables
			loadingCheckout: false,
			disableCheckout: false,
			loadingShipping: false,

			showCustomerDetails: false,
			showShippingDetails: false,
			showBillingDetails: false,

			savingShippingAddress: false,
			savingBillingAddress: false,

			showInternationalDisclaimer: false,

			hideShippingAddressForm: false,

			// Data
			guest: {
				email: '',
				firstName: '',
				lastName: '',
			},

			// Address Data
			saveNewShippingAddress: false,
			saveNewBillingAddress: false,

			// Order Data
			orderComments: '',

			// Google Maps
			googleMapsLoaded: false,
		}
	},

	async mounted() {
		this.loadingCheckout = true

		this.loadGoogleMapsScript()
			.then(() => {
				this.googleMapsLoaded = true
			})
			.catch((error) => {
				console.error('Error loading Google Maps script:', error)
			})

		this.guest = JSON.parse(JSON.stringify(this.checkout.guest))

		if (!this.checkout.shipping.selectedShipping?.id && !this.isDigitalOnly) {
			this.checkout.resetShipping()

			if (this.isLoggedIn) {
				await this.loadCheckoutData()
			} else if (!this.checkout.guest?.email) {
				this.showCustomerDetails = true
				this.loadingCheckout = false
			} else {
				this.showShippingDetails = true
				this.loadingCheckout = false
			}
		} else {
			this.loadingCheckout = false
		}

		this.validateBillingMatchesShipping()
	},

	methods: {
		// Customer Related
		handleCustomerContinue(email) {
			this.guest.email = email
			this.checkout.updateGuestEmail(email)

			console.log('customer added email to checkout: ', email)
			this.showCustomerDetails = false
			this.showShippingDetails = true
		},

		// Shipping Related
		async handleAddressSelected() {
			if (this.checkout.shipping.billingMatchesShipping) this.checkout.copyShippingToBilling()
			this.loadingShipping = true
			await this.getShippingOptions()
		},
		async handleShippingContinue() {
			if (this.saveNewShippingAddress) {
				this.savingShippingAddress = true

				const address = await this.saveNewAddress('shipping')
				if (address?.id) this.checkout.addressesPush(address)

				this.savingShippingAddress = false
			}

			if (this.checkout.showShippingDisclaimer) {
				this.showInternationalDisclaimer = true
			}

			this.showShippingDetails = false

			if (this.checkout.shipping.billingMatchesShipping) this.checkout.copyShippingToBilling()

			if (!this.checkout.billingAddressValidated.value) {
				this.showBillingDetails = true
			} else {
				this.$emit('showPayment')
			}
			this.$nextTick(() => {
				// Scroll to the top of the billing section
				document.getElementById('customerDetails').scrollIntoView({ behavior: 'smooth', block: 'start' })
			})
		},
		updateSaveNewShippingAddress(value) {
			this.saveNewShippingAddress = value
		},
		async getShippingOptions() {
			try {
				await helpers.getShippingEstimates()
			} catch (error) {
				console.error('Error fetching shipping options:', error)
				this.checkout.setShippingOptions([])
			} finally {
				this.loadingShipping = false
			}
		},

		// Billing Related
		async handleBillingContinue() {
			if (this.saveNewBillingAddress) {
				this.savingBillingAddress = true

				const address = await this.saveNewAddress('billing')
				if (address?.id) this.checkout.addressesPush(address)

				this.savingBillingAddress = false
			}
			this.showBillingDetails = false
			this.$emit('showPayment')

			this.$nextTick(() => {
				// Scroll to the top of the billing section
				document.getElementById('customerDetails').scrollIntoView({ behavior: 'smooth', block: 'start' })
			})
		},
		updateSaveNewBillingAddress(value) {
			this.saveNewBillingAddress = value
		},
		validateBillingMatchesShipping() {
			if (this.checkout.shipping.billingMatchesShipping && this.checkout.shippingAddressValidated.value && !this.checkout.billingAddressValidated.value) {
				this.checkout.updateBillingMatchesShipping(false)
			}
		},

		// General
		loadGoogleMapsScript() {
			return new Promise((resolve, reject) => {
				if (typeof google !== 'undefined') {
					resolve()
					return
				}

				const mapsApiKey = `AIzaSyARUX22fO86etfOvgUEl3IN-uAbZEY2UAs`

				const script = document.createElement('script')
				script.src = `https://maps.googleapis.com/maps/api/js?key=${mapsApiKey}&libraries=places,autocomplete-data`
				script.async = true
				script.onload = resolve
				script.onerror = reject
				document.head.appendChild(script)
			})
		},
		async loadCheckoutData() {
			this.loadingCheckout = true
			this.loadingShipping = true

			await this.fetchAddresses()

			this.loadingCheckout = false
		},
		async fetchAddresses() {
			const addresses = await Mango.addresses({ limit: 9999, search: { author: this.store?.user?.id } })

			for (let address of addresses) {
				delete address.author
			}
			this.checkout.initializeAddresses(addresses)
			console.log('addresses fetched')

			await this.getShippingOptions()

			this.hideShippingAddressForm = true

			this.showShippingDetails = true
		},
		async saveNewAddress(type) {
			console.log('saving new address to Mango....')

			let address
			if (type === 'shipping') {
				address = {
					...this.checkout.shipping.shippingAddress,
					bcCustomerId: this.store.user?.bcId,
				}
			} else {
				address = {
					...this.checkout.billing.billingAddress,
					bcCustomerId: this.store.user?.bcId,
				}
			}

			let response = await Mango.addresses.save(address, false, true)

			if (response?.response?.id && !response.warnings) {
				return response.response
			} else {
				console.log(response)
				Swal.fire({ text: `Error saving address: ${response.warnings.join(', ')}`, icon: 'error' })
			}
		},
		hashAddress(address) {
			if (!address) return ''
			return JSON.stringify({
				address1: address.address1,
				address2: address.address2,
				city: address.city,
				stateOrProvince: address.stateOrProvince,
				postalCode: address.postalCode,
				countryCode: address.countryCode,
			})
		},
	},

	computed: {
		isLoggedIn() {
			return !!this.store?.user?.id
		},
		showCustomerDetailsSlot() {
			if (this.showCustomerDetails) return true
			if (this.isLoggedIn && !this.store?.user?.email) {
				return true
			} else if (this.isLoggedIn && !!this.store?.user?.email) {
				return false
			} else if (!this.isLoggedIn && !this.guest.email) {
				return true
			} else if (!this.isLoggedIn && !!this.guest.email) {
				return false
			}
			return false
		},
	},

	watch: {
		'store.cart.bcId': {
			handler(newVal) {
				if (newVal && this.store?.user?.id) {
					this.loadCheckoutData()
				} else {
					this.loadingCheckout = false
				}
			},
			immediate: false,
			deep: true,
		},
		'store.user.id': {
			handler(newVal) {
				if (newVal) {
					this.showCustomerDetails = false
					this.loadCheckoutData()
				}
			},
			immediate: false,
			deep: true,
		},
		'guest.email': {
			handler: debounce(function (newVal) {
				this.checkout.updateGuestEmail(newVal)
			}, 300),
		},
	},
}
</script>

<style scoped></style>
